import { FrontendEnvironmentSettings } from '@act/shared/environment';
import { useEffect, useState } from 'react';
import { apiDomain } from 'toolkit/environment/apiDomain';
import { setEnvironment } from 'toolkit/environment/environment/environment.util';
import { initializeUserMonitoring } from 'toolkit/environment/environment/userMonitoring.util';

export type AppConfigStatus = 'loading' | 'ready' | 'error';

export type AppConfigState = {
  status: AppConfigStatus;
  errorMessage?: string;
};

const DEFAULT_CONFIG_ERROR =
  'We could not reach the server to load configuration. Check your connection and try again.';

/**
 * Loads `/client-config` from the API and initializes environment + monitoring.
 * @returns Loading, ready, or error state for app shell gating.
 */
export function useGetAppConfig(): AppConfigState {
  const [state, setState] = useState<AppConfigState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const configUrl = `${apiDomain}/client-config`;

    const loadConfig = async () => {
      const setError = (errorMessage: string) => {
        if (cancelled) {
          return;
        }

        setState({
          status: 'error',
          errorMessage,
        });
      };

      try {
        const response = await fetch(configUrl, {
          method: 'get',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          setError(
            `Failed to load app configuration (HTTP ${response.status}).`
          );
          return;
        }

        const bodyString = await response.text();
        let environmentSettings: FrontendEnvironmentSettings;
        try {
          environmentSettings = JSON.parse(
            bodyString
          ) as FrontendEnvironmentSettings;
        } catch {
          setError('Failed to parse app configuration.');
          return;
        }

        if (cancelled) {
          return;
        }

        setEnvironment(environmentSettings);
        initializeUserMonitoring(environmentSettings);
        setState({ status: 'ready' });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : DEFAULT_CONFIG_ERROR;
        setError(errorMessage);
      }
    };

    void loadConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
